/**
 * ContactCard — avatar tile used in carousels (Home, Favorites,
 * Recently Added) and the search results grid.
 *
 * Session 126t (fix #4) — converted the outer wrapper from a JS-
 * navigated <div role="button"> to a real <Link to={...}>. This
 * restores the native browser semantics that the role-button approach
 * silently broke:
 *   • Tab + Enter (keyboard navigation)
 *   • Screen reader announces "link" instead of "button"
 *   • Cmd/Ctrl-click → open contact in a new tab
 *   • Middle-click → open contact in a new tab
 *   • Right-click → browser context menu offers "Open in new tab"
 * The visual treatment is preserved via inline styles + className.
 */
import { memo } from 'react';
import { Link } from 'react-router-dom';
import { Star } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { contactDisplayName } from '../lib/contactName';

const ContactCard = memo(function ContactCard({ contact, size = 'default', sortBy = 'first_name' }) {
  const initials = `${(contact.first_name || '')[0] || ''}${(contact.last_name || '')[0] || ''}`.toUpperCase();
  const isSmall = size === 'small';

  // Handle both local uploads and external URLs
  const imageUrl = contact.image_url?.startsWith('/api')
    ? `${process.env.REACT_APP_BACKEND_URL}${contact.image_url}`
    : contact.image_url;

  const localName = (contact.local_display_name || '').trim();
  const displayName = contactDisplayName(contact);

  return (
    <Link
      to={`/contacts/${contact.id}`}
      className="contact-card flex flex-col items-center gap-2 no-underline text-current focus:outline-none focus-visible:ring-2 focus-visible:ring-nav-blue/60 rounded-lg"
      style={{ width: isSmall ? 100 : 140 }}
      data-testid={`contact-card-${contact.id}`}
    >
      <div className="relative">
        <Avatar className={`${isSmall ? 'h-16 w-16' : 'h-20 w-20'} ${contact.is_favorite ? 'avatar-ring' : 'ring-2 ring-slate-200'} transition-shadow`}>
          <AvatarImage src={imageUrl} alt={displayName} className="object-cover" loading="lazy" />
          <AvatarFallback className="bg-nav-blue text-white font-heading font-bold text-lg">
            {initials}
          </AvatarFallback>
        </Avatar>
        {contact.is_favorite && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-signal-yellow rounded-full flex items-center justify-center shadow-sm">
            <Star size={10} className="text-nav-blue" fill="currentColor" />
          </span>
        )}
      </div>
      <div className="text-center">
        {localName ? (
          <p className={`font-heading font-semibold text-foreground leading-tight ${isSmall ? 'text-xs' : 'text-sm'}`} data-testid={`contact-card-name-${contact.id}`}>
            {localName}
          </p>
        ) : sortBy === 'last_name' ? (
          <>
            <p className={`font-heading font-bold text-foreground leading-tight ${isSmall ? 'text-xs' : 'text-sm'}`}>
              {contact.last_name || ''}
            </p>
            <p className={`text-muted-foreground leading-tight ${isSmall ? 'text-[10px]' : 'text-xs'}`}>
              {contact.first_name || ''}
            </p>
          </>
        ) : (
          <>
            <p className={`font-heading font-semibold text-foreground leading-tight ${isSmall ? 'text-xs' : 'text-sm'}`}>
              {contact.first_name || ''}
            </p>
            <p className={`text-muted-foreground leading-tight ${isSmall ? 'text-[10px]' : 'text-xs'}`}>
              {contact.last_name || ''}
            </p>
          </>
        )}
      </div>
    </Link>
  );
});

export default ContactCard;
